import {
  ActionNames,
  NativeActionNames,
  NativeActionsList,
  StylesDerivedNativeActions,
  SystemActionsList,
  EditActionBase as SystemAction,
  HostEditActionDefinition,
  StylesDerivedNativeActionNames,
} from '@wix/editor-platform-host-integration-apis';
import {
  type Action,
  type DashboardAction,
  Data,
  DATA,
} from '@wix/component-protocol/schema';
import { CompRef } from '@wix/document-services-types';
import { CustomizedManifestAction } from '../types';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export const isObjectEmpty = (obj: object | null | undefined): boolean =>
  !obj || Object.keys(obj).length === 0;

export const hasDataType = (
  data: Data | null | undefined,
  dataType: keyof typeof DATA.DATA_TYPE,
) => {
  if (data && !isObjectEmpty(data)) {
    return Object.values(data).some((value) => value.dataType === dataType);
  }
  return false;
};

export const isSingleSelection = (editorPointers: EditorPointer<CompRef>[]) => {
  return editorPointers.length === 1;
};

export const isActionName = (actionName: string): actionName is ActionNames =>
  Object.values({ ...NativeActionsList, ...SystemActionsList }).some(
    (name) => name === actionName,
  );

export const isCustomizedNativeManifestAction = (
  action: Action | DashboardAction,
): action is CustomizedManifestAction => {
  return 'displayName' in action || 'execution' in action;
};

export const shouldBeExcludedFromEditActions = (
  action: SystemAction | HostEditActionDefinition,
): boolean => {
  return 'shouldBeExcluded' in action && !!action.shouldBeExcluded?.();
};

export const isNativeActionName = (
  actionName: string,
): actionName is NativeActionNames =>
  Object.values(NativeActionsList).some((name) => name === actionName);

export const isNativeStylesActionName = (
  actionName: string,
): actionName is StylesDerivedNativeActionNames =>
  Object.values(StylesDerivedNativeActions).some((name) => name === actionName);

export const objectMap = <T, U>(
  obj: Record<string, T>,
  fn: (value: T, key: string, index: number) => U,
): Record<string, U> =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value], index) => [
      key,
      fn(value, key, index),
    ]),
  );

export const objectFilter = <T>(
  obj: Record<string, T>,
  fn: (value: T, key: string, index: number) => boolean,
): Record<string, T> =>
  Object.fromEntries(
    Object.entries(obj).filter(([key, value], index) => fn(value, key, index)),
  );
